import React, { useState, useEffect } from 'react';
import { Card, Form, Row, Col, Select, Button, Spin } from 'antd';
import { OtherDRBranchWise, OtherDRDistrictWise} from './SavingService';
import { errHandler } from '../../helper/helper';
import { exportExcelBranchWise, exportExcelDistrictWise } from "../../Utils/OtherDepositsExport";

const { Option } = Select;

const OtherDReport = ({
    loginPlaceId,
    loading,
    products,
    districtData,
    districtName,
    divisionalId,
    branchId
}) => {
    const [selectedDistricts, setSelectedDistricts] = useState([]);
    const [availableBranches, setAvailableBranches] = useState([]);
    const [selectedBranches, setSelectedBranches] = useState([]);
    const [selectedProducts, setSelectedProducts] = useState([]);
    const [reportLoading, setReportLoading] = useState(false);

    const reportTypeConfig = {
        branchWise: {
            label: 'Branch Wise',
            disableBranch: false,
            disableDistrict: false
        },
        districtWise: {
            label: 'District Wise',
            disableBranch: true,
            disableDistrict: false
        },
    };

    const [selectedReportType, setSelectedReportType] = useState('districtWise');

    const [disableBranch, setDisableBranch] = useState(reportTypeConfig['districtWise'].disableBranch);
    const [disableDistrict, setDisableDistrict] = useState(reportTypeConfig['districtWise'].disableDistrict);

    const handleReportTypeChange = (value) => {
        setSelectedReportType(value);
        const config = reportTypeConfig[value];

        if (config) {
            setDisableBranch(config.disableBranch);
            setDisableDistrict(config.disableDistrict);
        } else {
            setDisableBranch(false);
            setDisableDistrict(false);
        }
    };

    const activeStateOptions = [
        { value: 0, label: '🟢 Active' },
        { value: 1, label: '🔴 Dormant' },
        { value: 2, label: '⚪ All' }
    ];
    const [selectedActiveState, setSelectedActiveState] = useState(0);

    const isBranchUser = loginPlaceId === "1";

    useEffect(() => {
        if (isBranchUser && branchId) {
            const branchIdNum = parseInt(branchId);
            setSelectedBranches([branchIdNum]);
            setAvailableBranches([{ id: branchIdNum, branchName: "Your Branch" }]);
            setSelectedReportType('branchWise');
        }
    }, [isBranchUser, branchId]);

    useEffect(() => {
        if (isBranchUser) return;

        const filtered = districtData
            .filter(d => selectedDistricts.includes(d.districtId))
            .flatMap(d => d.branchDtoList || []);
        setAvailableBranches(filtered);

        setSelectedBranches(prev =>
            prev.filter(bid =>
                filtered.some(b => b.id === bid)
            )
        );
    }, [selectedDistricts, districtData, isBranchUser]);

    useEffect(() => {
        if (selectedReportType === 'branchWise') {
            setSelectedDistricts(prev =>
                prev.length > 1 ? prev.slice(0, 1) : prev
            );
        }
    }, [selectedReportType]);

    const generateDistrictReport = async () => {
        let data = {
            districtIdList: selectedDistricts,
            productIdList: selectedProducts,
            activeStatus: selectedActiveState
        }
        const res = await OtherDRDistrictWise(data);
        return res;
    }

    const generateBranchReport = async () => {
        let data = {
            branchIdList: selectedBranches,
            productIdList: selectedProducts,
            activeStatus: selectedActiveState
        }
        const res = await OtherDRBranchWise(data);
        return res;
    }

    const generateReport = async () => {
        if (selectedReportType === 'branchWise') {
            return await generateBranchReport();
        } else if (selectedReportType === 'districtWise') {
            return await generateDistrictReport();
        } else {
            console.warn("Unsupported report type selected:", selectedReportType);
            return { data: [] };
        }
    };

    const handleExport = async () => {
        if (selectedReportType === 'branchWise' && selectedBranches.length === 0) {
            errHandler("Please select at least one branch");
            return;
        }

        if (selectedReportType === 'districtWise' && selectedDistricts.length === 0) {
            errHandler("Please select at least one district");
            return;
        }

        if (selectedProducts.length === 0) {
            errHandler("Please select at least one product");
            return;
        }

        setReportLoading(true);
        try {
            const apiData = await generateReport();
            const hasData = apiData?.data?.length > 0;

            if (hasData) {
                if (selectedReportType === 'branchWise') {
                    await exportExcelBranchWise({
                        apiData: apiData.data,
                        fileName: "Branch_Wise_Other_Deposits_Report.xlsx"
                    });
                } else if (selectedReportType === 'districtWise') {
                    await exportExcelDistrictWise({
                        apiData: apiData.data,
                        fileName: "District_Wise_Other_Deposits_Report.xlsx"
                    });
                }
            } else {
                errHandler("No data available to export.");
            }
        } catch (err) {
            errHandler(err);
        } finally {
            setReportLoading(false);
        }
    };

    return (
        <div className="panel-container">
            <Card hoverable className="inner-card">
                <Form layout="vertical">
                    <Row gutter={[16, 16]}>

                        <Col span={24}>
                            <Form.Item label="Select Active State">
                                <Select
                                    placeholder="Select active state"
                                    style={{ width: '100%' }}
                                    value={selectedActiveState}
                                    onChange={setSelectedActiveState}
                                    allowClear
                                >
                                    {activeStateOptions.map(option => (
                                        <Option key={option.value} value={option.value}>
                                            {option.label}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>

                        {!isBranchUser && (
                            <Col span={24}>
                                <Form.Item label="Select Report Type">
                                    <Select
                                        onChange={handleReportTypeChange}
                                        value={selectedReportType}
                                        style={{ width: '100%' }}
                                    >
                                        {Object.entries(reportTypeConfig).map(([key, config]) => (
                                            <Option key={key} value={key}>
                                                {config.label}
                                            </Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </Col>
                        )}

                        {!isBranchUser && (
                            <Col span={24}>
                                <Form.Item label="Select Districts">
                                    <Select
                                        mode={selectedReportType === 'branchWise' ? undefined : 'multiple'}
                                        placeholder="Select districts"
                                        style={{ width: '100%' }}
                                        value={
                                            selectedReportType === 'branchWise'
                                                ? selectedDistricts[0] || null
                                                : selectedDistricts
                                        }
                                        onChange={(value) => {
                                            if (selectedReportType === 'branchWise') {
                                                setSelectedDistricts(value ? [value] : []);
                                            } else {
                                                if (value.includes('ALL')) {
                                                    setSelectedDistricts(districtData.map(d => d.districtId));
                                                } else {
                                                    setSelectedDistricts(value);
                                                }
                                            }
                                        }}
                                        disabled={disableDistrict}
                                    >
                                        {selectedReportType === 'districtWise' && (
                                            <Option key="ALL" value="ALL">
                                                🔘 Select All Districts
                                            </Option>
                                        )}

                                        {districtData
                                            .filter(d =>
                                                loginPlaceId === "4" ? d.districtName === districtName : true
                                            )
                                            .map(d => (
                                                <Option key={d.districtId} value={d.districtId}>
                                                    🏙️ {d.districtName}
                                                </Option>
                                            ))}
                                    </Select>
                                </Form.Item>
                            </Col>
                        )}

                        {!isBranchUser && (
                            <Col span={24}>
                                <Form.Item label="Select Branch(es)">
                                    <Select
                                        mode="multiple"
                                        placeholder="Select branches"
                                        style={{ width: '100%' }}
                                        value={selectedBranches}
                                        onChange={(values) => {
                                            if (values.includes('ALL')) {
                                                setSelectedBranches(availableBranches.map(b => b.id));
                                            } else {
                                                setSelectedBranches(values);
                                            }
                                        }}
                                        disabled={availableBranches.length === 0 || disableBranch}
                                    >
                                        <Option key="ALL" value="ALL">
                                            🔘 Select All Branches
                                        </Option>
                                        {availableBranches.map(branch => (
                                            <Option key={branch.id} value={branch.id}>
                                                🏢 {branch.branchName}
                                            </Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </Col>
                        )}

                        {/* Multi-select product */}
                        <Col span={24}>
                            <Form.Item label="Select Products">
                                <Select
                                    mode="multiple"
                                    allowClear
                                    placeholder="Select products"
                                    style={{ width: '100%' }}
                                    value={selectedProducts}
                                    onChange={(values) => {
                                        if (values.includes('ALL')) {
                                            setSelectedProducts(
                                                products
                                                    .filter(p => p.productId !== 1 && p.productId !== 2)
                                                    .map(b => b.productId)
                                            );
                                        } else {
                                            setSelectedProducts(values);
                                        }
                                    }}
                                >
                                    <Option key="ALL" value="ALL">
                                        🔘 Select All Products
                                    </Option>

                                    {products
                                        .filter(p => p.productId !== 1 && p.productId !== 2)
                                        .map(product => (
                                            <Option key={product.productId} value={product.productId}>
                                                {product.productName}
                                            </Option>
                                        ))}
                                </Select>
                            </Form.Item>
                        </Col>

                        {/* Submit button */}
                        <Col span={24}>
                            <Form.Item className="generate-btn-outline">
                                <Button
                                    onClick={handleExport}
                                    disabled={reportLoading}
                                    className="generate-btn"
                                    style={{ textAlign: 'right' }}
                                >
                                    {reportLoading ? <Spin /> : 'Generate Report'}
                                </Button>
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>
            </Card>
        </div>
    );
};

export default OtherDReport;
